import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import Button from 'app/shared/components/button/Button';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import styledFuncs from 'app/styles/styledFuncs';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import IconLib from 'app/shared/components/icon/icon-lib';
import {
  useIdentity,
  useSetRepairersActivity,
  useUpdatePushNotification,
} from 'app/core/api/company/api-company';
import Icon from 'app/shared/components/icon/Icon';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { Switch } from '@material-ui/core';
import { PropsOf } from 'app/tools/general.types';
import clsx from 'clsx';
import { Merge } from 'type-fest';
import ClassName from 'app/styles/ClassName';
import { NotificationUpdateRequest } from 'app/core/api/repairer/api-repairer.types';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import { useLogout } from 'app/core/api/auth/api-auth';
import { PageDetails } from 'app/core/api/company/api-company.types';
import ProfileHeader from './components/ProfileHeader';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OnlineStatusBox = styled.div`
  margin: ${styledFuncs.pad(9.5)} ${styledFuncs.pad(1.5)} 0 !important;
  display: flex;
  border: 1px solid #e8e8e8;
  border-radius: 25px;
  background: ${p => p.theme.palette.misc.lightGrey};
`;
const Container = styled.div``;
type StatusButtonProps = { active?: boolean };
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StatusButton = styled(Button).attrs({
  iconButton: true,
})<StatusButtonProps>`
  font-size: ${styledFuncs.pxToRem(16)};
  color: #bdbdbd;
  font-weight: 600;
  flex: 1;
  ${p =>
    p.active &&
    css`
      color: ${p.theme.palette.primary.main};
      background: white;
      border-radius: 25px;
    `}
`;

const SectionBox: React.FC<{ title: string }> = ({
  title,
  children,
}) => (
  <>
    <h3
      className={clsx(
        'mt-1 mb-1 font-normal text-primary',
        ClassName.NO_HEIGHT_MARGIN
      )}
    >
      {title}
    </h3>
    <div className="flex flex-col rounded border border-solid border-neutral-200">
      {children}
    </div>
  </>
);

const SectionButton: React.VFC<
  Merge<
    PropsOf<typeof Button>,
    { title: string; subtitle: string; icon: IconLib }
  >
> = ({ title, subtitle, icon, ...buttonProps }) => (
  <Button variant="text" className="flex-grow h-btn" {...buttonProps}>
    <Icon {...{ icon }} />
    <div className="flex-grow ml-1">
      <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
        {title}
      </h4>
      <div className="text-xs font-light text-left line-clamp-2">
        {subtitle}
      </div>
    </div>
    <Icon icon={IconLib.CHEVRON_RIGHT} />
  </Button>
);

export default function BusinessProfile(): JSX.Element {
  useSetPageTitle('Profile');

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Business Profile',
    };
    repairActivity(pageDetails).subscribe();
  });
  const { resource: { company, user } = {}, request: loadIdentity } =
    useIdentity();
  const { request$: updateNotification } =
    useUpdatePushNotification();
  const SwitchStatus = () => {
    const req: NotificationUpdateRequest = {
      enablePushNotification: !user?.enablePushNotification,
    };
    updateNotification(req).subscribe(() => {
      loadIdentity();
    });
  };

  const { request$: logout$ } = useLogout();
  const logout = () => {
    logout$().subscribe();
  };
  const SwitchButton: React.VFC<
    Merge<
      PropsOf<typeof Button>,
      { title: string; subtitle: string; icon: IconLib }
    >
  > = ({ title, subtitle, icon, ...buttonProps }) => (
    <Button
      variant="text"
      className="flex-grow h-btn"
      {...buttonProps}
    >
      <Icon {...{ icon }} />
      <div className="flex-grow ml-1">
        <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
          {title}
        </h4>
        <div className="text-xs font-light text-left line-clamp-2">
          {subtitle}
        </div>
      </div>
      <Switch
        defaultChecked={user?.enablePushNotification}
        onChange={() => SwitchStatus()}
      />
    </Button>
  );

  return (
    <>
      <Container>
        <ProfileHeader />
        {/* <OnlineStatusBox>
        <StatusButton active>Online</StatusButton>
        <StatusButton>Offline</StatusButton>
      </OnlineStatusBox> */}
        <SectionBox title="Profile">
          <SectionButton
            title={
              company?.accountType === 'individual'
                ? 'Profile'
                : 'Business Profile'
            }
            subtitle="View and edit business details"
            to={
              company?.accountType === 'individual'
                ? BusinessRoutePath.REPAIR_PERSONAL_DETAIL
                : BusinessRoutePath.UPDATE_BUSINESS_PROFILE
            }
            icon={
              company?.accountType === 'individual'
                ? IconLib.USER
                : IconLib.BRIEFCASE
            }
          />
          <SectionButton
            title={
              company?.accountType === 'individual'
                ? 'Address'
                : 'Business Address'
            }
            subtitle="View and edit business address"
            to={
              company?.accountType === 'individual'
                ? BusinessRoutePath.REPAIR_PERSONAL_ADDRESS
                : BusinessRoutePath.UPDATE_BUSINESS_INFORMATION
            }
            icon={IconLib.HOME}
          />
        </SectionBox>
        <SectionBox title="Categories and Location">
          <SectionButton
            title="Repair Categories"
            subtitle="Add or remove an area where you offer repairs"
            to={BusinessRoutePath.UPDATE_REPAIR_CATEGORIES}
            icon={IconLib.LIST}
          />
          <SectionButton
            title="Repair Locations"
            subtitle="View and edit repair locations"
            to={BusinessRoutePath.UPDATE_REPAIR_LOCATIONS}
            icon={IconLib.LOCATION_PIN}
          />
        </SectionBox>
        <SectionBox title="Payment">
          <SectionButton
            title="Payment Information"
            subtitle="Add or remove payment options"
            to={BusinessRoutePath.PAYMENT}
            icon={IconLib.WALLET}
          />
        </SectionBox>
        <SectionBox title="Settings">
          <SwitchButton
            title="Push Notifications"
            subtitle={user?.enablePushNotification ? 'On' : 'Off'}
            icon={IconLib.BELL}
          />
          <SectionButton
            title="Change Login"
            subtitle="Choose a new password"
            to={SharedRoutePath.CHANGE_PASSWORD}
            icon={IconLib.LOCK}
          />
          <SectionButton
            title="Logout"
            subtitle="Logout of this device"
            onClick={logout}
            icon={IconLib.EXIT}
          />
        </SectionBox>
      </Container>
    </>
  );
}
